/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, styled, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { BackButton } from "../../components/BackButton";
import { Button } from "../../components/Button";
import ContactCard from "../../components/ContactCard";
import { PageContainer } from "../../components/layout/PageContainer";
import { Loader, LoadingBox } from "../../components/Loader";
import TitleBox from "../../components/TitleBox";
import YesNoAction from "../../components/YesNoAction";
import { usePermission } from "../../hooks/usePermission";
import { splitAddress } from "../../services/als/splitAddress";
import { useAppDispatch } from "../../store/rootStore";
import { TestIds } from "../../testIds";
import { CONTACT_CARD_TITLE } from "../../util/consts";
import { error } from "../../util/log";
import { ERequestStatus } from "../../util/types";
import { usePreviousPage } from "../../util/usePreviousPage";
import AuthSlice, { selectContactInfo, selectCurrentFarmerAccount } from "../azure-b2c/storage";
import { useDrawdownModal } from "../drawdown/v1/useDrawdownRequest";
import { getDrawdownConfig } from "../drawdown/v2/DrawdownRequestModal/api";
import {
  useDrawdownRequestContext,
  withDrawdownRequestContextProvider,
} from "../drawdown/v2/DrawdownRequestModal/DrawdownRequestContext";
import DrawdownRequestModal from "../drawdown/v2/DrawdownRequestModal/DrawdownRequestModal";
import { isDrawdownRequestable } from "../drawdown/v2/DrawdownRequestModal/utils";

import { AgreementTitle } from "./components/AgreementTitle";
import SalesAgreementDeliverySchedule from "./schedule/DeliverySchedule";
import DeliveryDetails from "./DeliveryDetails";
import SalesLine from "./SalesLine";
import SalesOrders from "./SalesOrders";
import { selectAgreementsRequestStatus, selectSalesAgreementById } from "./storage";
import { hasSchedule } from "./utils";

const Wrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  [theme.breakpoints.up("lg")]: {
    flexDirection: "row",
    gap: "16px",
  },
}));

const SaleAgreementContainer = styled(Box)(() => ({}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "40px",
  [theme.breakpoints.down("sm")]: {
    gap: "32px",
  },
}));

export const SalesAgreement = () => {
  const params = useParams();

  const salesAgreementId = params.salesAgreementId;

  const salesAgreement = useSelector(selectSalesAgreementById(salesAgreementId));
  const account = useSelector(selectCurrentFarmerAccount);
  const requestStatus = useSelector(selectAgreementsRequestStatus);
  const relationshipOwner = useSelector(selectContactInfo);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { previousPage } = usePreviousPage();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { setAgreement } = useDrawdownModal(); // v1 request drawdown modal

  const [drawdownRequest, setDrawdownRequest] = React.useState(false);
  const { state, setState } = useDrawdownRequestContext();
  const canCreateOrderFromAgreement = usePermission(
    "SalesAgreements_OrderFromAgreementCreate"
  ).allowed;

  React.useEffect(() => {
    if (salesAgreementId && account?.accountNumber && salesAgreement?.supportsDelivery) {
      getDrawdownConfig(salesAgreementId, account?.accountNumber).then((request) => {
        setState({
          drawdownConfig: request.data,
        });
      });
    }
  }, [salesAgreement]);

  const handleSalesAgreementRequestDelivery = (date?: Date) => {
    if (date) {
      setState({
        selectedMonth: {
          label: dayjs(date).format("MMMM YYYY"),
          value: date,
        },
      });
    }

    if (
      state.drawdownConfig &&
      state.drawdownConfig.deliveryType === "Delivery" &&
      account &&
      salesAgreement?.deliveryAddressRecordId
    ) {
      const address = account?.addresses.find(
        (e) => e.recordId === salesAgreement.deliveryAddressRecordId
      );
      if (address) {
        const { first } = splitAddress(address.fullAddress);
        setState({
          userEdited: {
            addressSelected: {
              address: address.fullAddress,
              addressName: address.name,
              streetAddress: first,
              postCode: address.zipCode,
              recordId: address.recordId,
              ...address,
            },
          },
        });
      }
    }
    setDrawdownRequest(true);
  };

  const onRequestDelivery = React.useCallback(
    (date?: Date) => {
      if (!salesAgreement) {
        error("Something is not right");
        return;
      }

      if (hasSchedule(salesAgreement.rawLines)) {
        handleSalesAgreementRequestDelivery(date);
      } else {
        setAgreement(salesAgreement);
      }
    },
    [salesAgreement]
  );

  const drawdownButton = React.useMemo(() => {
    if (canCreateOrderFromAgreement && isDrawdownRequestable(account, salesAgreement)) {
      const width = isSm ? "100%" : "auto";

      return (
        <Box alignSelf="flex-end" display="flex" ml="auto" width={width}>
          <Button
            variant="contained"
            sx={{ width, ml: "auto" }}
            onClick={() => onRequestDelivery()}
            data-testid={TestIds.feat.drawdown.requestButton}
          >
            Create order
          </Button>
        </Box>
      );
    }
  }, [canCreateOrderFromAgreement, isSm, onRequestDelivery, account, salesAgreement]);

  // Only displays if sales agreement is valid
  const contactCard = React.useMemo(() => {
    if (salesAgreement) {
      return (
        <Wrapper>
          <ContactCard
            title={CONTACT_CARD_TITLE.underAgreementDetails}
            testId={TestIds.components.contactCard.card}
            {...relationshipOwner}
          />
          {<DeliveryDetails {...salesAgreement} />}
        </Wrapper>
      );
    }
  }, [relationshipOwner, salesAgreement]);

  // Only displays if sales agreement is valid
  const memo = React.useMemo(() => {
    if (requestStatus === ERequestStatus.PENDING) {
      return (
        <LoadingBox>
          <Loader title="Loading details" />
        </LoadingBox>
      );
    } else if (
      account &&
      salesAgreement &&
      account?.accountNumber === salesAgreement?.accountNumber
    ) {
      return (
        <SaleAgreementContainer>
          <ContentContainer>
            <Box>
              <AgreementTitle
                agreementId={salesAgreement.agreementId}
                sxContainer={{ width: "100%", flexWrap: "wrap" }}
                title={salesAgreement.title ?? salesAgreement.id}
                category={salesAgreement.classification}
                status={salesAgreement.status}
                endDate={salesAgreement.endDate}
                percentage={salesAgreement.percentageFulfilled}
                startDate={salesAgreement.startDate}
                totalProducts={salesAgreement.lines.length}
                totalCost={salesAgreement.totalCost}
                drawdownButton={!isSm ? drawdownButton : undefined}
              >
                {isSm && drawdownButton}
              </AgreementTitle>

              <SalesLine data={salesAgreement.lines} />
              <SalesAgreementDeliverySchedule
                agreement={salesAgreement}
                account={account}
                onRequestDelivery={(date) => onRequestDelivery(date)}
              />
              <SalesOrders agreementId={salesAgreement.id} />
            </Box>
            {contactCard}
          </ContentContainer>
        </SaleAgreementContainer>
      );
    } else if (account) {
      return (
        <Box mb={2}>
          <YesNoAction
            display="Sales agreement not available"
            body={
              <span>
                <span>Account: {account?.accountName}</span>
                <br />
                <span>Account number: {account?.accountNumber}</span>
                <br />
                <br />
                Looks like this Sales Agreement is associated with a different account. If you have
                other accounts with Farmlands you can switch using the account selector.
              </span>
            }
            optionA={{
              label: "Switch back to " + account.accountName,
              onClick: () => {
                dispatch(AuthSlice.actions.setSelectedFarmerAccount(account.accountNumber));
              },
            }}
            optionB={{
              label: "Back to Sales Agreements",
              onClick: () => {
                navigate("/agreements");
              },
            }}
          />
        </Box>
      );
    } else {
      return (
        <>
          <TitleBox
            display="Sales Agreement not available"
            body="The Sales Agreement you are trying to view does not exist for this account."
          />
          <Wrapper>
            <ContactCard
              title={"Contact a Farmlands representative to discuss Sales Agreements."}
              testId={TestIds.components.contactCard.card}
              {...relationshipOwner}
            />
          </Wrapper>
        </>
      );
    }
  }, [salesAgreement, account, isSm, drawdownButton]);

  return (
    <PageContainer data-testid={TestIds.pages.salesAgreement}>
      <BackButton
        text="All Sales Agreements"
        onClick={() => {
          if (previousPage === "/agreements") {
            navigate(-1);
          } else {
            navigate("/agreements");
          }
        }}
      />
      <DrawdownRequestModal open={drawdownRequest} onClose={() => setDrawdownRequest(false)} />
      {memo}
    </PageContainer>
  );
};

export default withDrawdownRequestContextProvider(SalesAgreement);
